var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from "react";
import "./SessionView.component.css";
import { SessionViewSlideMenu } from "./session-view-slide-menu/SessionViewSlideMenu.component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { SwitchSessionDialog } from "../../dialogs/switch-session-dialog/SwitchSessionDialog.component";
import { busService } from "../../../modules/bus";
import { ChangeLocalUserInfoMessage, UserSpawnMessage } from "../../../messages/session/SessionMessages";
import { usersService } from "../../../modules/multiplayer/UsersService";
import { WorldContainer } from "../../components/world-container/WorldContainer.component";
import { startParticipantsListener, stopParticipantsListener, useParticipantsStore } from "../../store/participants.store";
import { InteractionControls } from "../../components/interaction-controls/InteractionControls.component";
import { SharedScreenSurface } from "./shared-screen-surface/SharedScreenSurface.component";
import { OptionsDialog } from "../../dialogs/options-dialog/OptionsDialog.component";
import { vrService } from "../../../modules/xr/VRService";
import { EnableWebXRMessage, WebXRPresentingMessage } from "../../../messages/xr/WebXRMessages";
import { AskOkMessage } from "flavr-common";
import { SessionViewUtils } from "./SessionView.util";
import { useSessionListStore } from "../../store/sessionlist.store";
import { ModelUploadService } from "../../services/model-upload.service";
import { ModelUploadDialog } from "../../dialogs/model-upload-dialog/ModelUploadDialog.component";
import { DebugOverlay } from "../../components/debug-overlay/DebugOverlay.component";
import { EventHistoryOverlay } from "./side-panels/event-history-list/event-history-overlay/EventHistoryOverlay.component";
import { useModelUploadStateStore } from "../../store/model-upload-state.store";
import { ModelUploadIndicator } from "./model-upload-indicator/ModelUploadIndicator.component";
import { ConferenceView } from "./conference-view/ConferenceView.component";
import { MenuButton } from "../../components/menu-button/MenuButton.component";
import { EventHistoryPanel } from "./side-panels/event-history-list/event-history-panel/EventHistoryPanel.component";
import { WebcamPreview } from "../../components/webcam-preview/WebcamPreview.component";
import { MessageService } from "../../services/message.service";
import { RightSidePanel } from "./side-panels/right-side-panel/RightSidePanel.component";
import { EditAnnotationDialog } from "../../dialogs/edit-annotation-dialog/EditAnnotationDialog.component";
import { useInteractionModelsStore, startInteractionModelsStoreListeners, stopInteractionModelsStoreListeners } from "../../store/interaction-models.store";
import { ModelSettingsControl } from "../../components/model-settings-control/ModelSettingsControl.component";
import { ttConfigButton, ttEnterVRButton, ttLeaveSessionButton, ttNumberOfParticipants, ttShowConferenceButton, ttSwitchSessionButton, ttUploadModelButton, ttUploadModelButtonDisabled, ttViewMenuButton } from "../../common-strings.util";
import { useTranslation } from "react-i18next";
import { localStreamingService } from "../../../modules/multiplayer/peer/LocalStreamingService";
import { renderManager } from "../../../modules/xr/RenderManager";
import { preferencesService } from "../../../modules/preferences/PreferencesService";
const LIVE_VIEW_SURFACE_UID = 2147483647;
export function SessionView() {
    const [currentDialog, setCurrentDialog] = useState(SessionViewUtils.SessionViewDialogType.none);
    const [panelState, setPanelState] = useState(SessionViewUtils.getPersistedPanelState());
    const [objectSettingsMinimized, setObjectSettingsMinimized] = useState(false);
    const [vrAvailable, setVrAvailable] = useState(false);
    const [vrActive, setVrActive] = useState(false);
    const liveViewActiveRef = useRef(false);
    const [currentSessionName, setCurrentSessionName] = useState("");
    const [currentUploadRequestFile, setCurrentUploadRequestFile] = useState(undefined);
    const [objectPopupVisible, setObjectPopupVisible] = useState(false);
    const otherUsers = useParticipantsStore((state) => state.otherUsers());
    const leaveVrButton = useRef(document.createElement("div"));
    const sessionListStore = useSessionListStore();
    const modelUploadInProgress = useModelUploadStateStore((state) => state.inProgress);
    const modelStore = useInteractionModelsStore();
    const { t } = useTranslation();
    useEffect(() => {
        void startInteractionModelsStoreListeners();
        const user = usersService.getLocalUser();
        busService.connection("SessionView").send(new ChangeLocalUserInfoMessage({
            customUsername: user.customUsername,
            avatar: undefined
        }));
        busService.connection("SessionView").send(new UserSpawnMessage());
        SessionViewUtils.removeLeaveVRButton();
        const setLiveViewEnabled = (enabled) => {
            renderManager.setBystanderViewEnabled(enabled);
            liveViewActiveRef.current = enabled;
            SessionViewUtils.updateLiveViewButton(leaveVrButton.current, enabled);
            return enabled;
        };
        const toggleLiveView = () => __awaiter(this, void 0, void 0, function* () { return setLiveViewEnabled(!liveViewActiveRef.current); });
        const isPeerLiveViewShared = () => localStreamingService.getPlayer3DViewStreams().some((stream) => stream.surfaceUid === LIVE_VIEW_SURFACE_UID);
        // Render and read back the live view only while someone can receive it.
        const syncPeerLiveViewRendering = () => {
            renderManager.setBystanderViewStreaming(isPeerLiveViewShared() && localStreamingService.hasConnectedPeers());
        };
        const togglePeerLiveView = () => __awaiter(this, void 0, void 0, function* () {
            if (isPeerLiveViewShared()) {
                localStreamingService.stopPlayer3DViewSharing(LIVE_VIEW_SURFACE_UID);
                syncPeerLiveViewRendering();
                return false;
            }
            try {
                yield localStreamingService.startPlayer3DViewSharing(LIVE_VIEW_SURFACE_UID, renderManager.bystanderView.captureStream());
                return true;
            }
            finally {
                syncPeerLiveViewRendering();
            }
        });
        localStreamingService.onUpdateDelegate.add(syncPeerLiveViewRendering);
        vrService.onVRSupportedDelegate.add((supported) => {
            setVrAvailable(supported);
        });
        setVrAvailable(vrService.isVRSupported);
        const leaveVRButton = SessionViewUtils.createLeaveVRButton(() => syncVRPresentation(false), toggleLiveView, togglePeerLiveView);
        leaveVrButton.current = leaveVRButton;
        let wasPresenting = false;
        const syncVRPresentation = (isPresenting) => {
            var _a;
            setVrActive(isPresenting);
            if (isPresenting) {
                if (!wasPresenting) {
                    setLiveViewEnabled((_a = preferencesService.getBooleanPreferredValue("autoStartVRLiveView", true)) !== null && _a !== void 0 ? _a : true);
                }
                document.body.appendChild(leaveVrButton.current);
            }
            else {
                setLiveViewEnabled(false);
                renderManager.setBystanderViewStreaming(false);
                localStreamingService.stopPlayer3DViewSharing(LIVE_VIEW_SURFACE_UID);
                SessionViewUtils.removeLeaveVRButton();
            }
            wasPresenting = isPresenting;
        };
        const connection = busService.connection("SessionView");
        const presentingHandler = connection.subscribeByType(WebXRPresentingMessage, (msg) => syncVRPresentation(msg.isPresenting));
        if (renderManager.isXRPresenting())
            syncVRPresentation(true);
        startParticipantsListener();
        return () => {
            stopInteractionModelsStoreListeners();
            connection.unsubscribe(presentingHandler);
            localStreamingService.onUpdateDelegate.remove(syncPeerLiveViewRendering);
            renderManager.setBystanderViewEnabled(false);
            renderManager.setBystanderViewStreaming(false);
            liveViewActiveRef.current = false;
            localStreamingService.stopPlayer3DViewSharing(LIVE_VIEW_SURFACE_UID);
            SessionViewUtils.removeLeaveVRButton();
            stopParticipantsListener();
        };
    }, []);
    useEffect(() => {
        const sessionName = localStorage.getItem("session");
        if (sessionName) {
            setCurrentSessionName(sessionName);
            sessionListStore.setCurrentSession(sessionListStore.sessions.find((s) => s.name === sessionName));
        }
    }, [sessionListStore.sessions]);
    useEffect(() => {
        localStorage.setItem(SessionViewUtils.panelStateKey, JSON.stringify(panelState));
        if (!objectPopupVisible) {
            setObjectSettingsMinimized(!panelState.showRightSidePanel);
        }
    }, [panelState]);
    useEffect(() => {
        const appRootElement = document.getElementById("app-root");
        if (!appRootElement) {
            return;
        }
        if (currentDialog === SessionViewUtils.SessionViewDialogType.options) {
            appRootElement.classList.add("options-dialog-open");
        }
        else {
            appRootElement.classList.remove("options-dialog-open");
        }
        return () => {
            appRootElement.classList.remove("options-dialog-open");
        };
    }, [currentDialog]);
    useEffect(() => {
        if (modelStore.currentModelUID) {
            setObjectPopupVisible(true);
        }
        else {
            setObjectPopupVisible(false);
            setObjectSettingsMinimized(!panelState.showRightSidePanel);
        }
    }, [modelStore.currentModelUID]);
    useEffect(() => {
        // Only mirror the UI state here: the EnableWebXRMessage itself is already handled by the WebXRModule.
        // Re-asking would start a second, concurrent requestSession() and fail with InvalidStateError.
        const connection = busService.connection("SessionView");
        const handler = connection.subscribeByType(EnableWebXRMessage, (msg) => {
            if (vrAvailable && msg.enable) {
                setVrActive(true);
                document.body.appendChild(leaveVrButton.current);
            }
        });
        return () => {
            connection.unsubscribe(handler);
        };
    }, [vrAvailable]);
    const enterVRMode = () => {
        setVrActive(true);
        busService
            .connection("SessionView")
            .askByMessageType(new EnableWebXRMessage(true), AskOkMessage)
            .catch((err) => {
            console.error("Error while enabling VR", err);
            setVrActive(false);
        });
        document.body.appendChild(leaveVrButton.current);
    };
    const onModelUploadRequest = (file) => {
        if (modelUploadInProgress) {
            // Should never happen, as we disable the dropzone when upload is in progress (See WorldContainer.component.tsx)
            console.error("Model upload already in progress");
            return;
        }
        if (ModelUploadService.isFileValid(file)) {
            setCurrentUploadRequestFile(file);
            setCurrentDialog(SessionViewUtils.SessionViewDialogType.modelUpload);
        }
    };
    const clearModelUploadRequest = () => {
        setCurrentUploadRequestFile(undefined);
        setCurrentDialog(SessionViewUtils.SessionViewDialogType.none);
    };
    const leaveSession = () => {
        void MessageService.showConfirmation(t("sessionview:confirm-leave-session-message", "Are you sure you want to leave this session?"), t("sessionview:confirm-leave-session-option-leave", "Leave"), t("sessionview:confirm-leave-session-option-stay", "Stay"), () => {
            window.location.search = "";
        });
    };
    const createLeftMenuButtons = (orientation) => {
        return (React.createElement(React.Fragment, null,
            React.createElement("button", { onClick: leaveSession, className: "icon-button", "data-tooltip": ttLeaveSessionButton() },
                React.createElement(FontAwesomeIcon, { icon: ["fas", "door-open"] })),
            React.createElement("button", { onClick: () => {
                    setCurrentDialog(SessionViewUtils.SessionViewDialogType.switchSession);
                }, className: "icon-button", "data-tooltip": ttSwitchSessionButton() },
                React.createElement(FontAwesomeIcon, { icon: ["fas", "stream"] })),
            orientation === "vertical" && React.createElement("div", { style: { flexGrow: "1" } }),
            React.createElement("button", { onClick: () => {
                    setCurrentDialog(SessionViewUtils.SessionViewDialogType.options);
                }, className: "icon-button allow-settings-during-training-dialog", "data-tooltip": ttConfigButton() },
                React.createElement(FontAwesomeIcon, { icon: ["fas", "cog"] }))));
    };
    const createLeftSlideMenu = () => (React.createElement(SessionViewSlideMenu, { position: "topleft", pinned: panelState.topLeftMenuPinned, onPinnedChanged: (pinned) => {
            setPanelState((prev) => {
                return Object.assign(Object.assign({}, prev), { topLeftMenuPinned: pinned });
            });
        } },
        createLeftMenuButtons("horizontal"),
        React.createElement("div", { className: "sessionview-slide-menu-separator" }),
        React.createElement("section", { className: "sessionview-slide-menu-sessionname" },
            React.createElement("h2", null, currentSessionName))));
    const createRightMenuButtons = (orientation) => {
        return (React.createElement(React.Fragment, null,
            orientation === "horizontal" && (React.createElement("button", { className: "icon-button", onClick: () => {
                    setPanelState((prev) => {
                        return Object.assign(Object.assign({}, prev), { showConference: !prev.showConference });
                    });
                }, "data-tooltip": ttShowConferenceButton(), "data-tooltip-position": "bottom-left" },
                React.createElement(FontAwesomeIcon, { icon: ["fas", "angle-double-down"] }))),
            React.createElement("div", { className: "icon-display-numbered", "data-tooltip": ttNumberOfParticipants((otherUsers === null || otherUsers === void 0 ? void 0 : otherUsers.length) || 0), "data-tooltip-position": "bottom-left" },
                React.createElement(FontAwesomeIcon, { icon: ["fas", "users"] }),
                React.createElement("span", null, (otherUsers === null || otherUsers === void 0 ? void 0 : otherUsers.length) || 0)),
            React.createElement("button", { onClick: () => {
                    setCurrentDialog(SessionViewUtils.SessionViewDialogType.modelUpload);
                }, className: "icon-button", disabled: modelUploadInProgress, "data-tooltip": ttUploadModelButton(), "data-tooltip-disabled": ttUploadModelButtonDisabled(), "data-tooltip-position": "bottom-left" },
                React.createElement(FontAwesomeIcon, { icon: ["fas", "cloud-upload-alt"] })),
            orientation === "vertical" && React.createElement("div", { style: { flexGrow: "1" } }),
            createViewMenuButton()));
    };
    const createRightSlideMenu = () => (React.createElement(SessionViewSlideMenu, { position: "topright", pinned: panelState.topRightMenuPinned, onPinnedChanged: (pinned) => {
            setPanelState((prev) => {
                return Object.assign(Object.assign({}, prev), { topRightMenuPinned: pinned });
            });
        } }, createRightMenuButtons("horizontal")));
    const createViewMenuButton = () => {
        return (React.createElement(MenuButton, { icon: "th", size: "medium", menuPlacement: "bottom-left", menuTitle: t("sessionview:button-view-options-menu-title", "Show / Hide views"), tooltip: ttViewMenuButton(), tooltipPosition: "bottom-left", options: [
                {
                    icon: "chalkboard-teacher",
                    text: t("sessionview:button-view-options-conference", "Conference"),
                    checked: panelState.showConference,
                    onClick: () => {
                        setPanelState((prev) => {
                            return Object.assign(Object.assign({}, prev), { showConference: !prev.showConference });
                        });
                    }
                },
                {
                    icon: "address-book",
                    text: t("sessionview:button-view-options-users-and-objects", "Userlist / Objects"),
                    checked: panelState.showRightSidePanel,
                    onClick: () => {
                        setPanelState((prev) => {
                            return Object.assign(Object.assign({}, prev), { showRightSidePanel: !prev.showRightSidePanel });
                        });
                    }
                },
                {
                    icon: "comments",
                    text: t("sessionview:button-view-options-chat-and-history", "Chat / History"),
                    checked: panelState.showLeftSidePanel,
                    onClick: () => {
                        setPanelState((prev) => {
                            return Object.assign(Object.assign({}, prev), { showLeftSidePanel: !prev.showLeftSidePanel });
                        });
                    }
                }
            ] }));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "sessionview" },
            !vrActive && (React.createElement(React.Fragment, null,
                React.createElement(EventHistoryPanel, { visible: panelState.showLeftSidePanel, onCloseClicked: () => {
                        setPanelState((prev) => {
                            return Object.assign(Object.assign({}, prev), { showLeftSidePanel: false });
                        });
                    }, onExitClicked: leaveSession, onSwitchSessionClicked: () => {
                        setCurrentDialog(SessionViewUtils.SessionViewDialogType.switchSession);
                    }, onConfigClicked: () => {
                        setCurrentDialog(SessionViewUtils.SessionViewDialogType.options);
                    } }),
                React.createElement("div", { className: `sessionview-history-overlay sessionview-transparent-overlay ${panelState.showLeftSidePanel ? "" : "sessionview-transparent-overlay-visible"}` },
                    React.createElement(EventHistoryOverlay, { onOpenSidePanelClicked: () => {
                            setPanelState((prev) => {
                                return Object.assign(Object.assign({}, prev), { showLeftSidePanel: true });
                            });
                        } })))),
            React.createElement("div", { className: "sessionview-central-column" },
                !vrActive && (React.createElement(ConferenceView, { visible: panelState.showConference, dropDownVisible: panelState.showRightSidePanel, showSessionName: !panelState.showLeftSidePanel, leftMenuBar: panelState.showLeftSidePanel ? undefined : createLeftMenuButtons("vertical"), rightMenuBar: panelState.showRightSidePanel ? undefined : createRightMenuButtons("vertical"), onShowClicked: () => {
                        setPanelState((prev) => {
                            return Object.assign(Object.assign({}, prev), { showConference: true });
                        });
                    }, onCloseClicked: () => {
                        setPanelState((prev) => {
                            return Object.assign(Object.assign({}, prev), { showConference: false });
                        });
                    } })),
                React.createElement("main", { className: "sessionview-content" },
                    React.createElement(WorldContainer, { allowFileDrop: true, vrMode: vrActive, onFileDropped: onModelUploadRequest },
                        React.createElement(SharedScreenSurface, null),
                        panelState.showLeftSidePanel || panelState.showConference || vrActive || createLeftSlideMenu(),
                        panelState.showRightSidePanel || panelState.showConference || vrActive || createRightSlideMenu(),
                        React.createElement(DebugOverlay, null),
                        React.createElement(ModelUploadIndicator, null)),
                    objectPopupVisible && (React.createElement("div", { className: `sessionview-object-popup ${panelState.showRightSidePanel ? "" : "sessionview-object-popup-standalone"}` },
                        React.createElement(ModelSettingsControl, { minimized: objectSettingsMinimized, onToggleMinimized: () => {
                                setObjectSettingsMinimized(!objectSettingsMinimized);
                            } }))))),
            !vrActive && (React.createElement(React.Fragment, null,
                React.createElement(RightSidePanel, { visible: panelState.showRightSidePanel && !vrActive, additionalButtons: createViewMenuButton(), onCloseClicked: () => {
                        setPanelState((prev) => {
                            return Object.assign(Object.assign({}, prev), { showRightSidePanel: false });
                        });
                    }, onUploadModelClicked: () => {
                        setCurrentDialog(SessionViewUtils.SessionViewDialogType.modelUpload);
                    }, onEnterVRClicked: enterVRMode }),
                React.createElement("div", { className: `sessionview-bottom-right-controls sessionview-transparent-overlay ${panelState.showRightSidePanel ? "" : "sessionview-transparent-overlay-visible"}` },
                    React.createElement(WebcamPreview, null),
                    React.createElement("div", { className: "sessionview-interaction-controls" },
                        vrAvailable && (React.createElement("button", { onClick: enterVRMode, "data-tooltip": ttEnterVRButton(), "data-tooltip-position": "top-left" },
                            React.createElement(FontAwesomeIcon, { icon: ["fas", "vr-cardboard"] }),
                            React.createElement("span", null, t("sessionview:button-enter-vr", "Enter VR")))),
                        React.createElement("button", { className: "icon-button", onClick: () => {
                                setPanelState((prev) => {
                                    return Object.assign(Object.assign({}, prev), { showRightSidePanel: true });
                                });
                            }, "data-tooltip": t("sessionview:button-show-users-and-objects", "Show userlist and objects"), "data-tooltip-position": "top-left" },
                            React.createElement(FontAwesomeIcon, { icon: ["fas", "angle-double-left"] })),
                        React.createElement(InteractionControls, { transparent: false })))))),
        React.createElement("div", { className: "sessionview-dialogs" },
            currentDialog === SessionViewUtils.SessionViewDialogType.switchSession && (React.createElement(SwitchSessionDialog, { onClose: () => {
                    setCurrentDialog(SessionViewUtils.SessionViewDialogType.none);
                } })),
            currentDialog === SessionViewUtils.SessionViewDialogType.options && (React.createElement(OptionsDialog, { onClose: () => {
                    setCurrentDialog(SessionViewUtils.SessionViewDialogType.none);
                } })),
            currentDialog === SessionViewUtils.SessionViewDialogType.modelUpload && (React.createElement(ModelUploadDialog, { onClose: clearModelUploadRequest, file: currentUploadRequestFile })),
            React.createElement(EditAnnotationDialog, null))));
}
